import { useState } from 'react';
import { GoalQuickAdd } from '../../components/GoalQuickAdd';
import { Link } from 'react-router-dom';
import type { ClinicalEvidence, GoalStatus, RiskAssessment, RiskItem, Session } from '@/domain/schema/v1';
import { RISK_KEYS } from '@/domain/schema/v1';
import { RISK_LABELS } from '@/domain/workflow/brief';
import { goalStatusLabel } from '@/domain/workflow/documents';
import { useAutosave } from '../../hooks/useAutosave';
import { useLoad } from '../../hooks/useLoad';
import { useService } from '../../hooks/useService';
import { ConfirmButton, SaveIndicator } from '../../components/fields';

// ---------------- 위험도 ----------------

const RISK_STATUS = [
  { value: 'not_assessed', label: '평가 안 함' },
  { value: 'denied', label: '부인' },
  { value: 'present', label: '있음' },
  { value: 'unclear', label: '불분명' },
] as const;

/** 위험도 (CLINICAL_DATA_MODEL §7): 매 회기 전체 체크를 강제하지 않는다. 승인은 상담자만. */
export function RiskSection({ sessionId }: { sessionId: string }) {
  const svc = useService();
  const { data, reload } = useLoad(() => svc.ensureRisk(sessionId), [svc, sessionId]);
  if (!data) return null;
  return <RiskEditor key={data.id} risk={data} onChanged={reload} />;
}

function RiskEditor({ risk, onChanged }: { risk: RiskAssessment; onChanged: () => void }) {
  const svc = useService();
  const a = useAutosave<RiskAssessment>(risk, (patch, rev) => svc.updateRisk(risk.id, patch, rev), {
    readOnly: risk.counselorApproved,
  });
  const v = a.value!;
  const locked = risk.counselorApproved;
  const anyFlag = RISK_KEYS.some((k) => v[k].status === 'present' || v[k].status === 'unclear');
  return (
    <details className="card" open={anyFlag}>
      <summary>
        <strong>위험도</strong>{' '}
        {locked ? <span className="badge badge-user">승인됨</span> : <span className="badge">미승인</span>}
        {anyFlag && <span className="badge badge-review">확인 필요 항목 있음</span>}
      </summary>
      <SaveIndicator status={a.status} error={a.error} />
      <p className="muted small">관련 내용이 나온 항목만 바꾸세요. 위험 관련 전사는 원음으로 확인하는 것이 좋습니다.</p>
      <ul className="items">
        {RISK_KEYS.map((k) => {
          const item: RiskItem = v[k];
          const set = (patch: Partial<RiskItem>) => a.set(k, { ...item, ...patch });
          return (
            <li key={k} className="risk-row">
              <span>{RISK_LABELS[k]}</span>
              <select
                aria-label={`${RISK_LABELS[k]} 상태`}
                value={item.status}
                disabled={locked}
                onChange={(e) => set({ status: e.target.value as RiskItem['status'] })}
              >
                {RISK_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <label className="check small">
                <input
                  type="checkbox"
                  disabled={locked}
                  checked={!!item.followUpRequired}
                  onChange={(e) => set({ followUpRequired: e.target.checked })}
                />{' '}
                다음 회기 확인
              </label>
              {(item.status === 'present' || item.status === 'unclear') && (
                <input
                  aria-label={`${RISK_LABELS[k]} 조치`}
                  placeholder="취한 조치"
                  readOnly={locked}
                  value={item.actionTaken ?? ''}
                  onChange={(e) => set({ actionTaken: e.target.value || undefined })}
                />
              )}
            </li>
          );
        })}
      </ul>
      {locked ? (
        <button type="button" onClick={() => void svc.reviseRisk(risk.id).then(onChanged)}>
          수정본 만들기
        </button>
      ) : (
        <ConfirmButton
          label="위험도 승인"
          confirmText="상담자 판단으로 승인합니다. 승인 후에는 수정본으로만 바꿀 수 있습니다."
          onConfirm={() =>
            void a
              .flush()
              .then(() => svc.approveRisk(risk.id))
              .then(onChanged)
          }
        />
      )}
    </details>
  );
}

// ---------------- 목표 진행 ----------------

const GOAL_STATUSES: GoalStatus[] = [
  'not_started',
  'exploring',
  'progressing',
  'maintaining',
  'achieved',
  'paused',
  'terminated',
];

/** 목표별 회기 진행 (CLINICAL_DATA_MODEL §10). 상태 변경은 상담자 승인으로만. */
export function GoalProgressSection({ session }: { session: Session }) {
  const svc = useService();
  const { data, reload } = useLoad(
    async () => ({
      goals: await svc.listGoals(session.clientId),
      progress: await svc.listGoalProgress({ sessionId: session.id }),
    }),
    [svc, session.id],
  );
  if (!data) return null;
  const goals = data.goals.filter((g) => !['achieved', 'terminated'].includes(g.status));
  return (
    <details className="card" open={goals.length === 0 || undefined}>
      <summary>
        <strong>상담목표 진행</strong>
        {goals.length === 0 && <span className="muted small"> 목표가 없습니다 — 여기서 바로 추가하세요</span>}
      </summary>
      {goals.map((g) => {
        const done = data.progress.filter((p) => p.goalId === g.id && p.approvedStatus);
        return (
          <GoalRow
            key={g.id}
            goalId={g.id}
            title={g.title}
            status={g.status}
            doneLabel={done.at(-1)?.approvedStatus}
            sessionId={session.id}
            onDone={reload}
          />
        );
      })}
      <GoalQuickAdd clientId={session.clientId} onAdded={reload} />
    </details>
  );
}

function GoalRow(props: {
  goalId: string;
  title: string;
  status: GoalStatus;
  doneLabel?: GoalStatus | undefined;
  sessionId: string;
  onDone: () => void;
}) {
  const svc = useService();
  const [next, setNext] = useState<GoalStatus>(props.status);
  const [note, setNote] = useState('');
  return (
    <div className="stack item-block">
      <div>
        <strong>{props.title}</strong> <span className="badge">{goalStatusLabel(props.status)}</span>
        {props.doneLabel && <span className="muted small"> · 이번 회기에 승인됨</span>}
      </div>
      <select aria-label={`${props.title} 상태`} value={next} onChange={(e) => setNext(e.target.value as GoalStatus)}>
        {GOAL_STATUSES.map((s) => (
          <option key={s} value={s}>
            {goalStatusLabel(s)}
          </option>
        ))}
      </select>
      <input
        aria-label={`${props.title} 진행 메모`}
        placeholder="진행 메모 (근거)"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      <button
        type="button"
        disabled={next === props.status && !note}
        onClick={() =>
          void svc
            .saveGoalProgress(props.goalId, props.sessionId, {
              proposedStatus: next,
              ...(note ? { progressNote: note } : {}),
            })
            .then((p) => svc.approveGoalProgress(p.id))
            .then(() => {
              setNote('');
              props.onDone();
            })
        }
      >
        진행 기록 승인
      </button>
    </div>
  );
}

// ---------------- 임상 근거 ----------------

const EVIDENCE_TYPES = [
  { value: 'CLIENT_REPORT', label: '내담자 진술' },
  { value: 'COUNSELOR_OBSERVATION', label: '상담자 관찰' },
  { value: 'HYPOTHESIS', label: '가설' },
  { value: 'FACT', label: '확인된 사실 (외부/문서 확인)' },
] as const;

/** 사실 / 내담자 진술 / 상담자 관찰 / 가설을 구분해 기록 (MASTER §2.6) */
export function EvidenceSection({ sessionId }: { sessionId: string }) {
  const svc = useService();
  const { data, reload } = useLoad(() => svc.listEvidence({ sessionId }), [svc, sessionId]);
  const [type, setType] = useState<ClinicalEvidence['evidenceType']>('CLIENT_REPORT');
  const [text, setText] = useState('');
  const [err, setErr] = useState<string | null>(null);
  return (
    <details className="card">
      <summary>
        <strong>임상 근거</strong> <span className="muted small">{data?.length ?? 0}건</span>
      </summary>
      <ul className="items">
        {data?.map((e) => (
          <li key={e.id}>
            <span className={`badge ev-${e.evidenceType}`}>
              {EVIDENCE_TYPES.find((t) => t.value === e.evidenceType)?.label}
            </span>{' '}
            {e.text}
            {e.needsVerification && !e.verified && <span className="badge badge-review">확인 필요</span>}
            {e.sourceSegmentIds?.[0] && (
              <Link className="small" to={`/sessions/${sessionId}?seg=${encodeURIComponent(e.sourceSegmentIds[0])}`}>
                {' '}
                근거 발언 보기
              </Link>
            )}
            {(e.evidenceType === 'CLIENT_REPORT' || e.evidenceType === 'FACT') &&
              (e.verified ? (
                <button
                  type="button"
                  className="link small"
                  onClick={() => void svc.setEvidenceVerified(e.id, false).then(reload)}
                >
                  ✓ 확인됨 (취소)
                </button>
              ) : (
                <button
                  type="button"
                  className="small"
                  onClick={() => void svc.setEvidenceVerified(e.id, true).then(reload)}
                >
                  {e.evidenceType === 'FACT' ? '사실 확인' : '내담자 진술 맞음'}
                </button>
              ))}
          </li>
        ))}
      </ul>
      <select
        aria-label="근거 종류"
        value={type}
        onChange={(e) => setType(e.target.value as ClinicalEvidence['evidenceType'])}
      >
        {EVIDENCE_TYPES.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
      <textarea
        aria-label="근거 내용"
        rows={2}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="상담자의 질문·해석은 내담자 진술이 아닙니다"
      />
      {err && <p className="warn small">{err}</p>}
      <button
        type="button"
        disabled={!text.trim()}
        onClick={() =>
          void svc
            .addEvidence(sessionId, { evidenceType: type, text: text.trim() })
            .then(() => {
              setText('');
              setErr(null);
              reload();
            })
            .catch((e: Error) => setErr(e.message))
        }
      >
        근거 추가
      </button>
    </details>
  );
}
